<template>
  <div class="container">
    <loading-spinner v-if="isLoading" :status="$t('envelope.loadingEnvelope')"></loading-spinner>
    <div v-else-if="showPasswordDialog">
      <password-protected
        :passwordStatus="passwordStatus"
        :getEnvelope="getEnvelope"
        :key="`ps-${passwordStatus.refresh}`"></password-protected>
    </div>
    <app-header :title="$store.getters.title"></app-header>
    <side-bar-info></side-bar-info>
    <document></document>
    <document-footer></document-footer>
    <help></help>
    <reject-document></reject-document>
    <widget-verify></widget-verify>
    <envelope-guide></envelope-guide>
  </div>
</template>

<script>
import Header from '@/components/Header'
import EnvelopeGuide from '@/components/EnvelopeGuide'
import PasswordProtected from '@/components/PasswordProtected'
import brandStyler from '@/utils/brand-styler'

import SideBarInfo from '@/components/SideBarInfo'
import Help from '@/components/Help'
import RejectDocument from '@/components/RejectDocument'
import WidgetVerify from '@/components/WidgetVerify'
import Document from '@/components/Document'
import DocumentFooter from '@/components/Footer'
import LoadingSpinner from '@/components/LoadingSpinner'

import * as Sentry from '@sentry/browser'

export default {
  name: 'envelope',
  components: {
    SideBarInfo,
    Help,
    RejectDocument,
    WidgetVerify,
    Document,
    DocumentFooter,
    'app-header': Header,
    EnvelopeGuide,
    PasswordProtected,
    LoadingSpinner
  },
  data () {
    return {
      isLoading: true,
      scrollTop: 0,
      envelopeStatus: null,
      isSigned: false,
      clientId: null,
      companyName: null,
      errorMessage: null,
      passwordStatus: {
        refresh: 1,
        required: false,
        title: null,
        password: null
      }
    }
  },
  mounted () {
    window.addEventListener('scroll', (event) => {
      this.toggleDocumentNav(event)
      this.toggleProgressBar(event)
    })

    this.$bus.$on('scrollBody', () => { // Scroll document to active field
      const header = document.querySelectorAll('header')
      const headerHeight = header[0].firstChild.clientHeight + 70
      const currentId = this.$store.state.activeFieldId
      const input = document.querySelectorAll(`.document-field[data-id="${currentId}"]`)
      if (input.length > 0) {
        const scrollAmount = this.getOffset(input[0])
        this.$SmoothScroll((scrollAmount - headerHeight), 500)
      }
    })
  },
  computed: {
    showPasswordDialog () {
      return this.passwordStatus.required
    },
    allowTour () {
      return parseInt(this.$store.getters.config('company_tour_enable'), 10)
    }
  },
  created () {
    if (this.$route.name === 'Preview') {
      window.IS_PREVIEW = true
    }

    this.getEnvelope()

    this.$bus.$on('nextDocument', () => { // TODO Make a watcher?
      const body = document.getElementsByTagName('body')[0]
      body.scrollTop = 0
      document.documentElement.scrollTop = 0
    })

    this.$bus.$on('loading', () => {
      this.isLoading = true
    })

    this.$bus.$on('loadingStop', () => {
      this.isLoading = false
    })

    this.$bus.$on('submitEnvelope', ({ response }) => {
      if (response.status === 'verify') {
        let redirectUrl = this.$store.getters.widgetRedirectUrl

        if (redirectUrl) {
          if (!redirectUrl.match(/^https:\/\/|^http:\/\//g)) redirectUrl = `http://${redirectUrl}` // Add http:// if not provided
          redirectUrl = new URL(redirectUrl) // Evaluate string as URL object

          window.location.replace(redirectUrl)
        } else {
          this.isSigned = true
          this.envelopeStatus = response.status
          this.$router.push({
            name: 'EnvelopeComplete',
            params: {
              status: this.envelopeStatus,
              clientId: this.clientId,
              companyName: this.companyName
            }
          }).catch(error => {
            console.error(error)
          })
        }
      } else {
        this.$store.dispatch('getEnvelope', { query: this.$route.query, password: this.passwordStatus.password, widgetCode: this.$route.params.widgetCode }).then((envelope) => {
          this.envelopeStatus = envelope.data.status

          if (response.status === 'rejected') { // TODO This needs to be more generic, isProcessed would be better
            this.isSigned = true
          } else if (response) {
            this.isSigned = response.status === 200
          } else {
            this.isSigned = true
          }

          this.isLoading = false
          if (envelope.data.envelope_redirect_url && response !== null) {
            window.location = envelope.data.envelope_redirect_url
          } else {
            this.$router.push({
              name: 'EnvelopeComplete',
              params: {
                status: this.envelopeStatus,
                clientId: this.clientId,
                companyName: this.companyName
              }
            }).catch(error => {
              console.error(error)
            })
          }
        })
      }
    })
  },
  methods: {
    getEnvelope (password) {
      this.$store.dispatch('getEnvelope', { query: this.$route.query, password, widgetCode: this.$route.params.widgetCode }).then((response) => {
        const data = response.data
        this.passwordStatus.required = false

        if (!this.$route.params.widgetCode) {
          this.isSigned = data.isSigned
          this.clientId = data.client.client_id
          this.envelopeStatus = data.status
          this.companyName = data.client.company_name
        } else {
          this.clientId = data.config.company_id
          this.companyName = data.config.company_name
        }

        if (!this.isSigned) {
          this.isLoading = false
          if (this.allowTour) this.$bus.$emit('openModal', 'tourModal')
        } else {
          this.isLoading = false
          this.$router.push({
            name: 'EnvelopeComplete',
            params: {
              status: response.data.status,
              clientId: response.data.client.client_id,
              companyName: response.data.client.company_name
            }
          }).catch(error => {
            console.error(error)
          })
        }

        if (password) this.passwordStatus.password = password

        brandStyler(data.colours)
      }).catch((error) => {
        this.isLoading = false

        if (error.response && error.response.status === 401) {
          this.passwordStatus.required = true
          this.passwordStatus.title = error.response.data.title
          this.passwordStatus.incorrect = !!password
          this.passwordStatus.refresh++ // TODO Fix reactvitity issues in a better way
        } else if (error.response) {
          this.errorMessage = error.response.data.message
          this.errorCode = error.response.status
          Sentry.captureException(error.response)
          this.$router.push({ name: 'PageError', params: { errorMessage: this.errorMessage, errorCode: this.errorCode } })
        } else {
          this.errorMessage = error.message || error
          this.errorCode = 0
          Sentry.captureException(error, {
            extra: error
          })

          this.$router.push({ name: 'PageError', params: { errorMessage: this.errorMessage, errorCode: this.errorCode } })
        }
      })
    },
    getOffset (el) {
      const element = el.getBoundingClientRect()
      return element.top + (window.scrollY || window.pageYOffset)
    },
    toggleDocumentNav () {
      const windowScrollHeight = window.scrollTop || window.pageYOffset
      if ((windowScrollHeight + window.innerHeight) >= document.body.offsetHeight) {
        this.$bus.$emit('scrolledToBottom', { isBottom: true })
      } else {
        this.$bus.$emit('scrolledToBottom', { isBottom: false })
      }
    },
    toggleProgressBar () {
      const scrollTop = window.scrollTop || window.pageYOffset
      if (scrollTop > this.scrollTop + 20) {
        this.$bus.$emit('scrollingDown')
      } else if (scrollTop < (this.scrollTop - 20)) {
        this.$bus.$emit('scrollingUp')
      }
      if (scrollTop === 0) {
        this.$bus.$emit('scrollingUp')
      }
      this.scrollTop = scrollTop
    }
  }
}
</script>

<style lang="sass" scoped>
  .overlay
    position: absolute
    top: 0
    left: 0
    height: 100%
    width: 100%
    background: rgba(0, 0, 0, 0.6)
    z-index: 999
  .float-right
    float: right
</style>
